import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { StaleIndicator } from '../../components/stale-indicator';
import { Skeleton } from '../../components/ui/skeleton';
import { ViewCard } from '../../components/view-card';
import { useMediaQuery } from '../../hooks/use-media-query';
import { alertsQuery, pinnedViewsQuery, sessionQuery, summaryQuery } from '../../lib/queries';
import type { Alert, AlertSeverity } from '../../lib/schemas';
import { cn } from '../../lib/utils';

export const Route = createFileRoute('/_auth/')({
  component: HomePage,
});

const BUNK_STATUS_LABELS: Record<string, string> = {
  clean: 'Clean',
  little_left: 'Little Left',
  good_bit_left: 'Good Bit Left',
  all_eaten: 'All Eaten',
  sloppy: 'Sloppy',
};

const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  urgent: 'border-l-red-500 bg-red-950/30',
  warning: 'border-l-amber-500 bg-amber-950/30',
  info: 'border-l-blue-400 bg-blue-950/30',
};

function KpiCard({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string | number;
  sublabel?: string;
}) {
  return (
    <div className="rounded-lg bg-ndd-900 p-4">
      <p className="text-sm text-ndd-400">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {sublabel && <p className="mt-0.5 text-xs text-ndd-400">{sublabel}</p>}
    </div>
  );
}

function AlertStrip({ alerts }: { alerts: Alert[] }) {
  if (alerts.length === 0) return null;
  const top3 = alerts.slice(0, 3);
  return (
    <Link to="/alerts" className="block space-y-2">
      {top3.map((alert) => (
        <div
          key={alert.alert_id}
          className={cn('rounded-lg border-l-4 px-3 py-2', SEVERITY_COLORS[alert.severity])}
        >
          <p className="text-sm font-medium">{alert.subject_name}</p>
          <p className="text-xs text-ndd-300">{alert.message}</p>
        </div>
      ))}
      {alerts.length > 3 && (
        <p className="text-center text-xs text-ndd-400">+{alerts.length - 3} more alerts</p>
      )}
    </Link>
  );
}

function QuickActions({ role }: { role: string }) {
  const canLog = role === 'farmer' || role === 'helper';

  return (
    <div className="grid grid-cols-2 gap-3">
      {canLog && (
        <Link
          to="/log/feeding"
          className="rounded-lg bg-ndd-800 py-5 text-center font-semibold hover:bg-ndd-700 active:bg-ndd-600"
        >
          Log Feeding
        </Link>
      )}
      {canLog && (
        <Link
          to="/log/event"
          className="rounded-lg bg-ndd-800 py-5 text-center font-semibold hover:bg-ndd-700 active:bg-ndd-600"
        >
          Log Event
        </Link>
      )}
      <a
        href="https://claude.ai"
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-lg bg-ndd-800 py-5 text-center font-semibold hover:bg-ndd-700 active:bg-ndd-600"
      >
        Ask Claude
      </a>
      <Link
        to="/views"
        className="rounded-lg bg-ndd-800 py-5 text-center font-semibold hover:bg-ndd-700 active:bg-ndd-600"
      >
        Views
      </Link>
    </div>
  );
}

function HomePage() {
  const { data: session } = useQuery(sessionQuery);
  const { data: summary, isLoading, dataUpdatedAt } = useQuery(summaryQuery);
  const { data: alertsData } = useQuery(alertsQuery);
  const { data: pinnedData } = useQuery(pinnedViewsQuery);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const pinnedViews = (pinnedData?.views ?? []).sort(
    (a, b) => (a.pin_order ?? 999) - (b.pin_order ?? 999)
  );

  const alerts = alertsData?.alerts ?? summary?.alerts ?? [];
  const alertCount = alerts.length;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg px-4 py-4 space-y-4">
        <div className="flex items-center justify-between py-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-6 w-12" />
        </div>
        <Skeleton className="h-28 w-full" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  // Desktop layout
  if (isDesktop) {
    return (
      <div className="space-y-6 max-w-4xl">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Alerts</h2>
            <AlertStrip alerts={alerts} />
            {alerts.length === 0 && <p className="text-sm text-ndd-500">No active alerts</p>}
          </div>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            {summary?.recent_activity && summary.recent_activity.length > 0 ? (
              <div className="rounded-lg bg-ndd-900 divide-y divide-ndd-800">
                {summary.recent_activity.slice(0, 8).map((activity) => (
                  <div key={activity.timestamp} className="px-4 py-3">
                    <p className="text-sm">{activity.description}</p>
                    <p className="mt-0.5 text-xs text-ndd-500">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ndd-500">No recent activity</p>
            )}
          </div>
        </div>

        {/* Pinned views */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Pinned Views</h2>
          {pinnedViews.length > 0 ? (
            <div className="grid grid-cols-2 gap-6">
              {pinnedViews.map((view) => (
                <ViewCard key={view.view_id} view={view} role={session?.role ?? 'viewer'} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div className="rounded-lg border border-dashed border-ndd-800 px-6 py-8 text-center">
                <p className="text-sm text-ndd-500">No pinned views yet</p>
                <Link to="/views" className="text-xs text-ndd-400 hover:text-ndd-300 mt-1 block">
                  Browse Views →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 px-4 py-4">
      {dataUpdatedAt > 0 && <StaleIndicator dataUpdatedAt={dataUpdatedAt} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">New Day Dairy</h1>
        {alertCount > 0 && (
          <Link
            to="/alerts"
            className="flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-sm font-bold"
          >
            <AlertTriangle size={14} />
            {alertCount}
          </Link>
        )}
      </div>

      {/* Today's Feeding */}
      <div className="rounded-lg bg-ndd-900 p-4">
        <h2 className="mb-2 text-sm font-medium text-ndd-400">Today&apos;s Feeding</h2>
        {summary?.today_feeding ? (
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-ndd-300">End weight</span>
              <span className="font-semibold">
                {summary.today_feeding.end_weight_lbs != null
                  ? `${summary.today_feeding.end_weight_lbs.toLocaleString()} lbs`
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ndd-300">Lbs/head</span>
              <span className="font-semibold">
                {summary.today_feeding.lbs_per_head != null
                  ? `${summary.today_feeding.lbs_per_head} lbs`
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ndd-300">Bunk</span>
              <span className="font-semibold">
                {summary.today_feeding.bunk_status
                  ? (BUNK_STATUS_LABELS[summary.today_feeding.bunk_status] ??
                    summary.today_feeding.bunk_status)
                  : '—'}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-ndd-300">No feeding logged today</p>
            {(session?.role === 'farmer' || session?.role === 'helper') && (
              <Link
                to="/log/feeding"
                className="rounded-lg bg-ndd-700 px-4 py-2 text-sm font-semibold hover:bg-ndd-600"
              >
                Log Now
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Herd KPIs */}
      {summary?.herd && (
        <div className="grid grid-cols-2 gap-3">
          <KpiCard label="Total Herd" value={summary.herd.total} />
          <KpiCard label="Milking" value={summary.herd.milking} />
          <KpiCard label="Dry" value={summary.herd.dry} />
          <KpiCard label="Close-up" value={summary.herd.close_up} />
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium text-ndd-400">Alerts</h2>
            <Link to="/alerts" className="flex items-center gap-1 text-xs text-ndd-400">
              View all <ChevronRight size={12} />
            </Link>
          </div>
          <AlertStrip alerts={alerts} />
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="mb-2 text-sm font-medium text-ndd-400">Quick Actions</h2>
        <QuickActions role={session?.role ?? 'viewer'} />
      </div>

      {/* Recent Activity */}
      {summary?.recent_activity && summary.recent_activity.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-ndd-400">Recent Activity</h2>
          <div className="rounded-lg bg-ndd-900 divide-y divide-ndd-800">
            {summary.recent_activity.slice(0, 5).map((activity) => (
              <div key={activity.timestamp} className="px-4 py-3">
                <p className="text-sm">{activity.description}</p>
                <p className="mt-0.5 text-xs text-ndd-500">
                  {new Date(activity.timestamp).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pinned Views (mobile) */}
      {pinnedViews.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-ndd-400">Pinned Views</h2>
          <div className="space-y-4">
            {pinnedViews.map((view) => (
              <ViewCard key={view.view_id} view={view} role={session?.role ?? 'viewer'} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
